import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatTime } from '../utils/formatters';

const TABS = ['overview', 'apps', 'validators', 'super validators', 'transactions'];
const AGENT_TAB = 'chat';
const AGENT_TAB_LABEL = 'Cantonstats AI';

const MOBILE_TAB_LABELS = {
  'overview': 'Overview',
  'apps': 'Apps',
  'validators': 'Validators',
  'super validators': 'SV',
  'transactions': 'TX',
};
const MOBILE_AGENT_LABEL = 'AI';

export function Header({ activeTab, setActiveTab, loading, lastUpdate }) {
  const navigate = useNavigate();
  const [logoAnimating, setLogoAnimating] = useState(false);

  const handleGettingStarted = () => {
    navigate('/connect-node');
  };

  const handleLogoClick = () => {
    setLogoAnimating(true);
    setTimeout(() => {
      setLogoAnimating(false);
      setActiveTab('overview');
    }, 400);
  };

  return (
    <>
      <header style={{
        borderBottom: '1px solid var(--border-default)',
        padding: '12px 16px',
        position: 'sticky',
        top: 0,
        background: '#0a0c10',
        zIndex: 1000,
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '24px',
        }}>
          {/* Logo */}
          <div
            onClick={handleLogoClick}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              flexShrink: 0,
              cursor: 'pointer',
            }}
          >
            <img
              src="/logo_hq_v2.png"
              alt="CantonStats Logo"
              style={{
                width: '32px',
                height: '32px',
                transition: 'transform 0.3s ease',
                transform: logoAnimating ? 'scale(1.1)' : 'scale(1)',
              }}
            />
            <div>
              <div style={{
                fontWeight: '600',
                fontSize: '16px',
                letterSpacing: '-0.5px',
                color: 'var(--text-primary)',
                fontFamily: 'var(--font-sans)',
              }}>
                Cantonstats
              </div>
              <div style={{
                fontSize: '9px',
                color: 'var(--text-tertiary)',
                letterSpacing: '1px',
                textTransform: 'uppercase',
              }}>
                <span className="hide-mobile">Network Intelligence</span>
                <span className="show-mobile-only">Intelligence</span>
              </div>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hide-mobile" style={{
            display: 'flex',
            gap: '4px',
            flex: 1,
            justifyContent: 'center',
          }}>
            {TABS.map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{
                  background: activeTab === tab ? 'var(--bg-subtle)' : 'transparent',
                  border: 'none',
                  color: activeTab === tab ? 'var(--text-primary)' : 'var(--text-tertiary)',
                  padding: '8px 14px',
                  fontSize: '13px',
                  cursor: 'pointer',
                  textTransform: 'capitalize',
                  transition: 'all 0.15s ease',
                  fontWeight: activeTab === tab ? '500' : '400',
                  borderRadius: '6px',
                  fontFamily: 'var(--font-sans)',
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== tab) e.currentTarget.style.color = 'var(--text-secondary)';
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== tab) e.currentTarget.style.color = 'var(--text-tertiary)';
                }}
              >{tab}</button>
            ))}
            <button
              onClick={() => setActiveTab(AGENT_TAB)}
              style={{
                background: activeTab === AGENT_TAB ? 'var(--bg-subtle)' : 'transparent',
                border: 'none',
                color: activeTab === AGENT_TAB ? 'var(--text-primary)' : 'var(--text-tertiary)',
                padding: '8px 14px',
                fontSize: '13px',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                fontWeight: activeTab === AGENT_TAB ? '500' : '400',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontFamily: 'var(--font-sans)',
              }}
              onMouseEnter={(e) => {
                if (activeTab !== AGENT_TAB) e.currentTarget.style.color = 'var(--text-secondary)';
              }}
              onMouseLeave={(e) => {
                if (activeTab !== AGENT_TAB) e.currentTarget.style.color = 'var(--text-tertiary)';
              }}
            >
              <img
                src="/logo_hq_v2.png"
                alt=""
                style={{
                  width: '14px',
                  height: '14px',
                  opacity: activeTab === AGENT_TAB ? 0.9 : 0.5,
                }}
              />
              {AGENT_TAB_LABEL}
            </button>
          </nav>

          {/* Status + CTA */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            flexShrink: 0,
          }}>
            {/* Status - desktop */}
            <div className="hide-mobile" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '11px',
              color: 'var(--text-tertiary)',
              fontFamily: 'var(--font-mono)',
            }}>
              <span style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background: loading ? 'var(--text-tertiary)' : 'var(--accent)',
                boxShadow: loading ? 'none' : '0 0 6px rgba(0, 255, 136, 0.5)',
                animation: loading ? 'pulse 1s infinite' : 'none',
              }} />
              <span>
                {loading ? 'Syncing...' : formatTime(lastUpdate.getTime())}
              </span>
            </div>

            {/* Mobile status dot */}
            <span className="show-mobile-only" style={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              background: loading ? 'var(--text-tertiary)' : 'var(--accent)',
              boxShadow: loading ? 'none' : '0 0 6px rgba(0, 255, 136, 0.5)',
              animation: loading ? 'pulse 1s infinite' : 'none',
            }} />

            {/* CTA Button */}
            <button
              onClick={handleGettingStarted}
              style={{
                background: 'var(--text-primary)',
                border: 'none',
                color: 'var(--bg-primary)',
                padding: '10px 20px',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                fontFamily: 'var(--font-sans)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = '0.9';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '1';
              }}
            >
              <span className="hide-mobile">Connect Node</span>
              <span className="show-mobile-only">Connect</span>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <nav className="show-mobile-only" style={{
          display: 'flex',
          gap: '2px',
          marginTop: '12px',
          justifyContent: 'space-between',
        }}>
          {TABS.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                background: activeTab === tab ? 'var(--bg-subtle)' : 'transparent',
                border: 'none',
                color: activeTab === tab ? 'var(--text-primary)' : 'var(--text-tertiary)',
                padding: '8px 6px',
                fontSize: '11px',
                cursor: 'pointer',
                fontWeight: activeTab === tab ? '500' : '400',
                borderRadius: '4px',
                fontFamily: 'var(--font-sans)',
              }}
            >{MOBILE_TAB_LABELS[tab]}</button>
          ))}
          <button
            onClick={() => setActiveTab(AGENT_TAB)}
            style={{
              background: activeTab === AGENT_TAB ? 'var(--bg-subtle)' : 'transparent',
              border: 'none',
              color: activeTab === AGENT_TAB ? 'var(--text-primary)' : 'var(--text-tertiary)',
              padding: '8px 6px',
              fontSize: '11px',
              cursor: 'pointer',
              fontWeight: activeTab === AGENT_TAB ? '500' : '400',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontFamily: 'var(--font-sans)',
            }}
          >
            <img
              src="/logo_hq_v2.png"
              alt=""
              style={{
                width: '12px',
                height: '12px',
                opacity: activeTab === AGENT_TAB ? 0.9 : 0.5,
              }}
            />
            {MOBILE_AGENT_LABEL}
          </button>
        </nav>
      </header>
    </>
  );
}
